// Iran local time — the ONE place this client turns an epoch into a calendar day
// or a wall clock.
//
// The platform rule: EPOCH MILLISECONDS on the wire, everywhere, always
// (GraphQL carries them as strings because GraphQL Int is 32-bit). Iran local
// time exists only where a *day boundary* or a *wall clock* is needed. The
// scheduling service and parsina.bff.dentist compute the same boundary with the
// same offset; if this file drifts from them, the day sheet and the day it asks
// for stop being the same day.
//
// Pure and React-free on purpose — tests/iranTime.test.js calls it directly.

// UTC+3:30. Iran abolished DST in 2022, so this is a constant, not a lookup.
// Taken from the framework rather than re-declared: two copies of an offset is
// how a day sheet ends up half an hour wide.
export { IRAN_OFFSET_MS } from '@parsina/framework/ui/lib/jalali';
import { IRAN_OFFSET_MS } from '@parsina/framework/ui/lib/jalali';

export const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Today in Iran, as `YYYY-MM-DD`. */
export function todayInIran(nowMs = Date.now()) {
  return new Date(nowMs + IRAN_OFFSET_MS).toISOString().slice(0, 10);
}

/** The Iran-local calendar day an epoch falls on, as `YYYY-MM-DD`. */
export function iranDateOf(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '';
  return new Date(n + IRAN_OFFSET_MS).toISOString().slice(0, 10);
}

/** The Iran wall clock of an epoch, as `HH:MM` (24-hour, both locales). */
export function iranClockOf(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '';
  return new Date(n + IRAN_OFFSET_MS).toISOString().slice(11, 16);
}

/** Minutes past Iran midnight for an epoch. null when there is no usable time. */
export function iranMinutesOf(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return null;
  const d = new Date(n + IRAN_OFFSET_MS);
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}

/** Minutes past Iran midnight, right now. */
export const nowMinutesInIran = (nowMs = Date.now()) => iranMinutesOf(nowMs);

/**
 * Shift an ISO date (`YYYY-MM-DD`) by N days.
 *
 * Anchored at noon UTC, which is what keeps a ±1-day step from landing on the
 * previous date through rounding. Returns '' for anything that is not a date,
 * so a hand-edited `?date=` in the URL cannot produce `NaN-NaN-NaN`.
 */
export function shiftISO(iso, days) {
  if (!ISO_DATE_RE.test(iso || '')) return '';
  const ms = Date.parse(`${iso}T12:00:00Z`) + days * 86_400_000;
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * An Iran-local ISO day as an epoch, at noon — the instant to hand a date
 * formatter when all that matters is *which day* it is.
 */
export function isoDayToMs(iso) {
  if (!ISO_DATE_RE.test(iso || '')) return null;
  const ms = Date.parse(`${iso}T12:00:00Z`);
  return Number.isNaN(ms) ? null : ms;
}

/** `HH:MM` → minutes past midnight. */
export function toMinutes(hhmm) {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}
